import {
  useGetMediaDownloadUrlQuery,
  useGetMediaQuery,
} from "@package/ui-gql/index-data-access";
import { useWallProvider } from "@package/ui-wall";
import { getMediaType } from "@package/ui-wall-engine";
import { useEffect, useRef, type PropsWithChildren } from "react";

type WipeAwayForegroundMediaProps = {
  media?: string;
  onMediaElementRendered: (
    mediaElement: HTMLVideoElement | HTMLImageElement,
  ) => void;
};

export function WipeAwayForegroundMedia({
  media,
  onMediaElementRendered,
  children,
}: PropsWithChildren<WipeAwayForegroundMediaProps>) {
  const provider = useWallProvider();
  const ref = useRef<HTMLDivElement>(null);

  const { data: dataMedia } = useGetMediaQuery({
    variables: {
      id: media ?? "",
    },
    skip: !media,
  });
  const { data: dataUrl } = useGetMediaDownloadUrlQuery({
    variables: {
      fileName: dataMedia?.item?.file ?? "",
    },
    skip: !dataMedia?.item?.file,
  });

  useEffect(
    function effectOnDataMediaImage() {
      if (dataMedia?.item && getMediaType(dataMedia.item) === "image") {
        const img = document?.createElement("img");

        const onLoad = () => {
          img.removeEventListener("load", onLoad);
          onMediaElementRendered(img);
        };

        img.src = dataUrl?.item?.url ?? "";
        img.addEventListener("load", onLoad);
        ref.current?.appendChild(img);

        return;
      }
    },
    [dataMedia?.item, dataUrl?.item?.url, onMediaElementRendered],
  );

  useEffect(
    function effectOnDataMediaVideo() {
      if (dataMedia?.item && getMediaType(dataMedia.item) === "video") {
        const video = document.createElement("video");

        let videoAnimationHandle: number | null = null;
        const videoOnLoadedData = () => {
          if (typeof videoAnimationHandle === "number") {
            video.cancelVideoFrameCallback(videoAnimationHandle);
          }
          step();
        };
        const step = () => {
          onMediaElementRendered(video);
          videoAnimationHandle = video.requestVideoFrameCallback(step);
        };

        video.src = dataUrl?.item?.url ?? "";
        video.autoplay = true;
        video.crossOrigin = "anonymous";
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.addEventListener("loadeddata", videoOnLoadedData);
        ref.current?.appendChild(video);
        video.load();
        video.play();

        return;
      }
    },
    [dataMedia?.item, dataUrl?.item?.url, onMediaElementRendered, provider],
  );

  return (
    <div className="absolute top-0 left-0 w-full h-full">
      <div ref={ref} className="absolute top-0 left-0 w-full h-full hidden" />
      {children}
    </div>
  );
}
